import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { observer } from 'mobx-react';
import { parse, stringify } from 'query-string';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { v4 as uuid } from 'uuid';
import { useEventLogging } from '../../../models/Logging';
import { useActionItemComposer, useNoteComposer, useUserSession } from '../../../models/hooks/appStateHooks';
import { ActionItemViewModel, ActionItemsViewModel, NoteViewModel } from '../../../viewmodels/AppViewModels';
import ActionItemsPlaceholderIconUrl from '../../assets/actionItemsListPlaceholderIcon.svg';
import { Button } from '../../components-v2/Button';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { FabCalloutPlaceholder } from '../../components/FabCalloutPlaceholder';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Portal, PortalDestination } from '../../components/Portal';
import { ITabViewChild, TabView } from '../../components/TabView';
import { ActionItemDetailsFlyout } from '../../components/actionItems/ActionItemDetailsFlyout';
import { ActionItemsCalendarView } from '../../components/actionItems/ActionItemsCalendarView';
import { ActionItemsFilterValue } from '../../components/actionItems/ActionItemsFilter';
import { ActionItemsList, IActionItemsListFilters } from '../../components/actionItems/ActionItemsList';
import { invalidateAllCalendarActionItems } from '../../components/calendar/hooks/useCalendarActionItems';
import { NotesList } from '../../components/notes/NotesList';
import { TableLayoutIcon } from '../../components/svgs/icons/TableLayoutIcon';
import { ScheduleMeetingIcon } from '../../components/svgs/icons/ScheduleMeetingIcon';
import { brandPrimary, navigation } from '../../styles/colors';
import { baseStyleSheet } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

export enum NotesActionItemsSelectedTabOption {
	Notes = 'notes',
	ActionItems = 'action-items',
	Completed = 'completed',
}

interface IProps {
	className?: string;
}

const NOTES_ACTION_ITEMS_PORTAL_ID = 'notes-action-items-tab-view-header';

function NotesActionItems({ className }: IProps) {
	const scrollToBottomPortalId = React.useRef(uuid());
	const location = useLocation<any>();
	const history = useHistory();
	const userSession = useUserSession();
	const noteComposer = useNoteComposer();
	const actionItemComposer = useActionItemComposer();
	const { logEvent, logApiError } = useEventLogging('NotesActionItems');
	const [actionItems] = React.useState(new ActionItemsViewModel(userSession));
	const [actionItemsCalendarView, setActionItemsCalendarView] = React.useState(false);
	// Search + scope live in the list's header (single source of truth); the calendar mirrors them.
	const [sharedActionItemFilters, setSharedActionItemFilters] = React.useState<IActionItemsListFilters>({
		searchQuery: '',
	});
	// Editing a list item opens the shared flyout (consistent with the calendar) rather than the
	// full-screen composer modal. The `?actionItemId` deep-link path still uses the modal fallback.
	const [flyoutActionItem, setFlyoutActionItem] = React.useState<Api.IActionItem | null>(null);
	const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);
	const [flyoutStartInEdit, setFlyoutStartInEdit] = React.useState(false);
	// Row click opens the flyout in read view; ⋯ Edit opens it straight in the editor.
	const openActionItemFlyout = React.useCallback((vm: ActionItemViewModel, startInEdit: boolean) => {
		setFlyoutActionItem(vm.toActionItem());
		setFlyoutStartInEdit(startInEdit);
		setIsFlyoutOpen(true);
	}, []);
	// `flyoutActionItem` is a one-shot snapshot, so after an edit reload it from the server to keep
	// the read view (checkbox/due/assignee/title) in sync; also invalidate the calendar queries.
	const onFlyoutChanged = React.useCallback(() => {
		invalidateAllCalendarActionItems();
		if (!flyoutActionItem?.id) {
			return;
		}
		const vm = new ActionItemViewModel(userSession, { id: flyoutActionItem.id });
		const applyReload = () => {
			setFlyoutActionItem(vm.toActionItem());
		};
		const ignore = () => {};
		Promise.resolve(vm.load()).then(applyReload, ignore);
	}, [flyoutActionItem, userSession]);
	const searchParams = parse(location.search);
	const activeTab =
		(searchParams.activeTab as NotesActionItemsSelectedTabOption | null) ?? NotesActionItemsSelectedTabOption.Notes;
	const actionItemId = searchParams.actionItemId;
	const noteId = searchParams.noteId;
	// When the tab changes, keep all the rest of the query params intact and just change the activeTab
	const redirectToTab = React.useCallback(
		(tab: NotesActionItemsSelectedTabOption) => {
			if (!history) {
				return;
			}
			const url = `/notes-action-items?${stringify({ ...searchParams, activeTab: tab })}`;
			history.replace(url);
		},
		[history, searchParams]
	);
	const closeModal = (sParamKey: string) => () => {
		if (!history || searchParams[sParamKey] == null) {
			return;
		}
		const sParams = { ...searchParams };
		delete sParams[sParamKey];
		history.replace(`/notes-action-items?${stringify(sParams)}`);
	};
	const closeNoteModal = closeModal('noteId');
	const closeActionItemModal = closeModal('actionItemId');

	React.useEffect(() => {
		const load = async () => {
			const noteVm = new NoteViewModel(userSession, { id: noteId });
			logEvent('LoadNote', noteVm);
			try {
				await noteVm.load();

				noteComposer.show(noteVm, closeNoteModal);
			} catch (error) {
				logApiError('LoadNote', error);
			}
		};
		if (noteId) {
			load();
		}
		return () => {
			if (noteId == null) {
				noteComposer.reset();
			}
		};
	}, [noteId, noteComposer, actionItemComposer, logEvent, logApiError, userSession, closeNoteModal]);
	React.useEffect(() => {
		const load = async () => {
			const actionItemVm = new ActionItemViewModel(userSession, { id: actionItemId });
			logEvent('LoadActionItem', actionItemVm);
			try {
				await actionItemVm.load();

				actionItemComposer.show(actionItemVm, closeActionItemModal);
			} catch (error) {
				logApiError('LoadActionItem', error);
			}
		};
		if (actionItemId) {
			load();
		}
		return () => {
			if (actionItemId == null) {
				actionItemComposer.reset();
			}
		};
	}, [actionItemId, noteComposer, actionItemComposer, logEvent, logApiError, userSession, closeActionItemModal]);
	const onRenderActionItemsListPlaceholder = (inProgress: boolean) =>
		function ActionItemsListPlaceholder(isEmpty: boolean, isLoading: boolean) {
			if (isLoading) {
				return <LoadingSpinner className='action-items-container-loading' type='large' />;
			}
			if (isEmpty) {
				return (
					<FabCalloutPlaceholder
						iconUrl={ActionItemsPlaceholderIconUrl}
						showFabPointer={inProgress}
						subtitle={inProgress ? 'Add a reminder from the blue round button on the bottom right.' : undefined}
						title={inProgress ? 'You don’t have any reminders.' : 'You don’t have any completed reminders.'}
					/>
				);
			}
			return null;
		};
	const handleEditNote = (id: string) => {
		const newSearchParams = { ...searchParams, noteId: id };
		history.replace(`/notes-action-items?${stringify(newSearchParams)}`);
	};
	const actionItemsViewToggle = (
		<>
			<Button
				variant='ghost'
				size='icon'
				aria-label='List view'
				aria-pressed={!actionItemsCalendarView}
				title='List view'
				onClick={() => setActionItemsCalendarView(false)}
			>
				<TableLayoutIcon fillColor={actionItemsCalendarView ? navigation : brandPrimary} />
			</Button>
			<Button
				variant='ghost'
				size='icon'
				aria-label='Calendar view'
				aria-pressed={actionItemsCalendarView}
				title='Calendar view'
				onClick={() => setActionItemsCalendarView(true)}
			>
				<ScheduleMeetingIcon fill={actionItemsCalendarView ? brandPrimary : navigation} />
			</Button>
		</>
	);

	const getTabViewContent = (): ITabViewChild[] => {
		return [
			{
				content: <NotesList scrollToBottomPortalId={scrollToBottomPortalId.current} onEditNote={handleEditNote} />,
				tabbarItem: {
					content: 'Notes',
					onSelect: ev => {
						ev.preventDefault();
						redirectToTab(NotesActionItemsSelectedTabOption.Notes);
					},
				},
			},
			{
				content: (
					<div className={css(viewStyleSheet.tab)}>
						{/* List stays mounted so its header (search + scope) is the single source of truth for
						    both views; in calendar mode it renders header-only and the calendar sits below. */}
						<ActionItemsList
							actionItemsVm={actionItems}
							headerAccessory={actionItemsViewToggle}
							headerPortalDestination={NOTES_ACTION_ITEMS_PORTAL_ID}
							hideHeaderDateAndSort={actionItemsCalendarView}
							showListBody={!actionItemsCalendarView}
							onEditActionItem={vm => openActionItemFlyout(vm, true)}
							onOpenActionItem={vm => openActionItemFlyout(vm, false)}
							onFiltersChanged={setSharedActionItemFilters}
							initialSelectedFilterValue={ActionItemsFilterValue.CurrentUser}
							onRenderPlaceholder={onRenderActionItemsListPlaceholder(true)}
							scrollToBottomPortalId={scrollToBottomPortalId.current}
							showSearchBar={true}
							userSession={userSession}
						/>
						{actionItemsCalendarView ? (
							<ActionItemsCalendarView
								assignedToUserId={sharedActionItemFilters.assignedToUserId}
								search={sharedActionItemFilters.searchQuery}
							/>
						) : null}
					</div>
				),
				tabbarItem: {
					content: 'Action Items',
					onSelect: ev => {
						ev.preventDefault();
						redirectToTab(NotesActionItemsSelectedTabOption.ActionItems);
					},
				},
			},
			{
				content: (
					<ActionItemsList
						actionItemsVm={actionItems}
						headerPortalDestination={NOTES_ACTION_ITEMS_PORTAL_ID}
						initialSelectedFilterValue={ActionItemsFilterValue.CurrentUser}
						onEditActionItem={vm => openActionItemFlyout(vm, true)}
						onOpenActionItem={vm => openActionItemFlyout(vm, false)}
						onRenderPlaceholder={onRenderActionItemsListPlaceholder(false)}
						scrollToBottomPortalId={scrollToBottomPortalId.current}
						showCompletedItems={true}
						showSearchBar={true}
						userSession={userSession}
					/>
				),
				tabbarItem: {
					content: 'Completed',
					onSelect: ev => {
						ev.preventDefault();
						redirectToTab(NotesActionItemsSelectedTabOption.Completed);
					},
				},
			},
		];
	};

	return (
		<div className={`${css(styleSheet.container)} notes-container ${className || ''}`}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Notes & Actions</h1>
			</Portal>
			<TabView
				className='notes-action-items-tab-view'
				hasFixedHeaders={true}
				headerPortalDestinationId={NOTES_ACTION_ITEMS_PORTAL_ID}
				selectedTabIndex={
					{
						[NotesActionItemsSelectedTabOption.Notes]: 0,
						[NotesActionItemsSelectedTabOption.ActionItems]: 1,
						[NotesActionItemsSelectedTabOption.Completed]: 2,
					}[activeTab]
				}
			>
				{getTabViewContent}
			</TabView>
			<PortalDestination id={scrollToBottomPortalId.current} />
			{flyoutActionItem ? (
				<ActionItemDetailsFlyout
					isOpen={isFlyoutOpen}
					setIsOpen={setIsFlyoutOpen}
					actionItem={flyoutActionItem}
					startInEdit={flyoutStartInEdit}
					onChanged={onFlyoutChanged}
				/>
			) : null}
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
}

const viewStyleSheet = StyleSheet.create({
	tab: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		minWidth: 0,
	},
});

export const NoteActionItemsContainer = observer(NotesActionItems);
